import { format, formatDistanceToNow } from 'date-fns'
import {
  AlertCircle,
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  Clock,
  RefreshCw,
} from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { toast } from 'react-hot-toast'
import { Link, useLocation } from '#router'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { trpc } from '@/lib/trpc'
import type { ClientFormData, CoachingClient } from '../problems/types'
import { HomeworkSection } from './homework-section'
import { NotesSection } from './notes-section'
import { PersonalDetails } from './personal-details'
import { PlatformUserLink } from './platform-user-link'
import { RecommendationEngineSection } from './recommendation-engine'

interface ClientDetailProps {
  client: CoachingClient | null
  loading: boolean
  error: string | null
  onUpdate: (data: Partial<ClientFormData>) => Promise<unknown>
  onAssignHomework: (
    type:
      | 'PROBLEM'
      | 'EXERCISE'
      | 'TASK'
      | 'SYSTEM_DESIGN'
      | 'SOURCE_CONTENT'
      | 'OTHER',
    data: {
      problemId?: string
      exerciseId?: string
      systemDesignProblemId?: string
      taskId?: string
      sourceContentId?: string
      title?: string
      description?: string
      url?: string
      notes?: string
    },
  ) => Promise<void>
  onRemoveHomework: (assignmentId: string) => Promise<void>
  onToggleDoneBefore: (questionId: string, doneBefore: boolean) => Promise<void>
  onUpdateLastAttempt: (
    questionId: string,
    lastAttemptDate: string | null,
  ) => Promise<void>
  onRefresh?: () => void
  onCheckIn: () => Promise<void>
}

function formatElapsedTime(
  lastCheckInAt: Date | string | null | undefined,
): string {
  if (!lastCheckInAt) return 'No check-ins yet'

  const checkInDate =
    typeof lastCheckInAt === 'string' ? new Date(lastCheckInAt) : lastCheckInAt
  const now = new Date()
  const diffMs = now.getTime() - checkInDate.getTime()

  if (diffMs < 0) return 'Just now'

  const seconds = Math.floor(diffMs / 1000)
  const minutes = Math.floor(seconds / 60)
  const hours = Math.floor(minutes / 60)
  const days = Math.floor(hours / 24)

  if (days > 0) return `${days}d ${hours % 24}h ${minutes % 60}m`
  if (hours > 0) return `${hours}h ${minutes % 60}m ${seconds % 60}s`
  if (minutes > 0) return `${minutes}m ${seconds % 60}s`
  return `${seconds}s`
}

export function ClientDetail({
  client,
  loading,
  error,
  onUpdate,
  onAssignHomework,
  onRemoveHomework,
  onToggleDoneBefore,
  onUpdateLastAttempt,
  onRefresh,
  onCheckIn,
}: ClientDetailProps) {
  const [elapsedTime, setElapsedTime] = useState<string>('')
  const [isActivityCollapsed, setIsActivityCollapsed] = useState(true)
  const [isCheckingIn, setIsCheckingIn] = useState(false)
  const [isManageOpen, setIsManageOpen] = useState(false)
  const [isDemoteOpen, setIsDemoteOpen] = useState(false)

  const { navigate } = useLocation()
  const utils = trpc.useUtils()
  const demoteMutation = trpc.admin.clients.demoteUser.useMutation({
    onSuccess: async () => {
      // Drop the archived client from the Direct-Coach list (and refresh the
      // roster) BEFORE navigating, so the list we land on is already correct
      // instead of serving stale cache until the next background refetch.
      await Promise.all([
        utils.admin.clients.list.invalidate(),
        utils.admin.users.list.invalidate(),
      ])
      toast.success('Client demoted to freemium')
      navigate('/admin/coaching')
    },
    onError: (err) => toast.error(err.message || 'Failed to demote client'),
  })

  // Calculate average days between check-ins
  const averageDaysBetweenCheckIns = useMemo(() => {
    if (!client?.activityLog || client.activityLog.length < 2) {
      return null
    }

    // Sort check-ins chronologically (oldest first)
    const sortedCheckIns = [...client.activityLog]
      .map((entry) => {
        const date =
          typeof entry.checkedInAt === 'string'
            ? new Date(entry.checkedInAt)
            : entry.checkedInAt
        return date.getTime()
      })
      .sort((a, b) => a - b)

    // Calculate differences between consecutive check-ins
    const differences: number[] = []
    for (let i = 1; i < sortedCheckIns.length; i++) {
      const diffMs = sortedCheckIns[i] - sortedCheckIns[i - 1]
      const diffDays = diffMs / (1000 * 60 * 60 * 24)
      differences.push(diffDays)
    }

    // Calculate average
    const sum = differences.reduce((acc, val) => acc + val, 0)
    return sum / differences.length
  }, [client?.activityLog])

  // Update elapsed time every second
  useEffect(() => {
    if (!client?.lastCheckInAt) {
      setElapsedTime('No check-ins yet')
      return
    }

    const updateElapsed = () => {
      setElapsedTime(formatElapsedTime(client.lastCheckInAt))
    }

    updateElapsed()
    const interval = setInterval(updateElapsed, 1000)

    return () => clearInterval(interval)
  }, [client?.lastCheckInAt])

  const handleCheckIn = useCallback(async () => {
    try {
      setIsCheckingIn(true)
      await onCheckIn()
    } catch (error) {
      console.error('Failed to check in:', error)
    } finally {
      setIsCheckingIn(false)
    }
  }, [onCheckIn])

  const baselineEpochQuery = trpc.admin.clients.getBaselineEpoch.useQuery(
    { clientId: client?.id ?? '' },
    { enabled: !!client?.id },
  )
  const setBaselineEpochMutation =
    trpc.admin.clients.setBaselineEpoch.useMutation()
  const updateBaselineEpochMutation =
    trpc.admin.clients.updateBaselineEpoch.useMutation()
  const clearBaselineEpochMutation =
    trpc.admin.clients.clearBaselineEpoch.useMutation()

  const refetchBaselineEpochWithToast = useCallback(async () => {
    const refetch = await baselineEpochQuery.refetch()
    if (refetch.error) {
      throw refetch.error
    }
  }, [baselineEpochQuery])

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-muted-foreground flex items-center gap-3">
          <RefreshCw size={20} className="animate-spin" />
          <span>Loading client...</span>
        </div>
      </div>
    )
  }

  if (error || !client) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <AlertCircle size={48} className="mx-auto mb-4 text-rose-400" />
          <h2 className="text-foreground mb-2 text-xl font-semibold">
            Client Not Found
          </h2>
          <p className="text-muted-foreground mb-4">
            {error || "The client you're looking for doesn't exist."}
          </p>
          <Link
            href="/admin/coaching"
            className="bg-primary text-primary-foreground inline-flex items-center gap-2 rounded-lg px-4 py-2 hover:opacity-90"
          >
            <ArrowLeft size={16} />
            Back to Clients
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="bg-background/95 border-border sticky top-0 z-10 border-b backdrop-blur-sm">
        <div className="w-full px-6 py-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <Link
                href="/admin/coaching"
                className="text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg p-2 transition-colors"
              >
                <ArrowLeft size={20} />
              </Link>
              <div>
                <h1 className="text-foreground text-xl font-bold">
                  {client.name}
                </h1>
                <p className="text-muted-foreground text-sm">Client Details</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              {client.lastCheckInAt && (
                <div className="text-muted-foreground flex items-center gap-2 text-sm">
                  <Clock size={16} />
                  <span className="font-mono">{elapsedTime}</span>
                </div>
              )}
              <Button
                type="button"
                onClick={handleCheckIn}
                disabled={isCheckingIn}
                size="sm"
              >
                {isCheckingIn ? (
                  <>
                    <RefreshCw size={16} className="mr-2 animate-spin" />
                    Checking in...
                  </>
                ) : (
                  'Check in'
                )}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="w-full space-y-6 px-4 py-6">
        {/* Personal Details Section */}
        <PersonalDetails client={client} onUpdate={onUpdate} />

        {/* Platform User */}
        <div className="bg-card border-border rounded-lg border p-4">
          <h2 className="text-foreground mb-3 text-lg font-semibold">
            Platform User
          </h2>
          <PlatformUserLink
            clientId={client.id}
            linkedUserId={client.userId}
            onLinked={() => onRefresh?.()}
          />
        </div>

        {/* Notes Section */}
        <NotesSection notes={client.notes} onUpdate={onUpdate} />

        {/* Homework Section */}
        <HomeworkSection
          client={client}
          onAssignHomework={onAssignHomework}
          onRemoveHomework={onRemoveHomework}
          onToggleDoneBefore={onToggleDoneBefore}
          onUpdateLastAttempt={onUpdateLastAttempt}
          onRefresh={onRefresh}
        />

        {/* Activity Log Section */}
        <div className="bg-card border-border rounded-lg border">
          <button
            type="button"
            onClick={() => setIsActivityCollapsed(!isActivityCollapsed)}
            className="hover:bg-muted/50 flex w-full items-center justify-between p-4 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-gradient-to-br from-purple-500 to-pink-600 p-2">
                <Clock size={20} className="text-white" />
              </div>
              <div className="text-left">
                <h2 className="text-foreground text-lg font-semibold">
                  Activity Log
                </h2>
                <p className="text-muted-foreground text-sm">
                  {client.activityLog?.length || 0} check-in
                  {client.activityLog?.length !== 1 ? 's' : ''}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {averageDaysBetweenCheckIns !== null && (
                <span className="text-muted-foreground text-sm">
                  Avg:{' '}
                  <span className="font-medium">
                    {averageDaysBetweenCheckIns.toFixed(1)} days
                  </span>
                </span>
              )}
              {isActivityCollapsed ? (
                <ChevronDown size={20} className="text-muted-foreground" />
              ) : (
                <ChevronUp size={20} className="text-muted-foreground" />
              )}
            </div>
          </button>
          {!isActivityCollapsed && (
            <div className="border-border border-t p-4">
              {!client.activityLog || client.activityLog.length === 0 ? (
                <div className="py-8 text-center">
                  <div className="bg-muted mb-4 inline-flex h-12 w-12 items-center justify-center rounded-full">
                    <Clock size={24} className="text-muted-foreground" />
                  </div>
                  <h3 className="text-foreground mb-2 font-medium">
                    No check-ins yet
                  </h3>
                  <p className="text-muted-foreground mb-4 text-sm">
                    Click "Check in" in the header to log your first activity.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {client.activityLog.map((entry) => {
                    const checkInDate =
                      typeof entry.checkedInAt === 'string'
                        ? new Date(entry.checkedInAt)
                        : entry.checkedInAt
                    return (
                      <div
                        key={entry.id}
                        className="border-border flex items-center justify-between rounded-lg border p-3"
                      >
                        <div className="flex items-center gap-3">
                          <div className="bg-primary/10 rounded-full p-2">
                            <Clock size={16} className="text-primary" />
                          </div>
                          <div>
                            <p className="text-foreground text-sm font-medium">
                              Check-in
                            </p>
                            <p className="text-muted-foreground text-xs">
                              {formatDistanceToNow(checkInDate, {
                                addSuffix: true,
                              })}
                            </p>
                          </div>
                        </div>
                        <div className="text-muted-foreground text-xs">
                          {format(checkInDate, 'MMM d, yyyy h:mm a')}
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Recommendation Engine Section */}
        <RecommendationEngineSection
          clientId={client.id}
          clientName={client.name}
          baselineEpochAt={baselineEpochQuery.data?.baselineEpochAt ?? null}
          baselineEpochTimeZone={baselineEpochQuery.data?.timeZone ?? null}
          engineEnabled={baselineEpochQuery.data?.engineEnabled ?? false}
          onSetBaselineEpoch={async (date) => {
            try {
              await setBaselineEpochMutation.mutateAsync({
                clientId: client.id,
                date,
                acknowledgedWarning: true,
              })
              await refetchBaselineEpochWithToast()
              toast.success('Baseline epoch set. Recommendation engine is on.')
            } catch (error) {
              const message =
                error instanceof Error
                  ? error.message
                  : 'Could not set baseline epoch.'
              toast.error(`Failed to set baseline epoch: ${message}`)
              throw error
            }
          }}
          onUpdateBaselineEpoch={async (date) => {
            try {
              await updateBaselineEpochMutation.mutateAsync({
                clientId: client.id,
                date,
                acknowledgedWarning: true,
              })
              await refetchBaselineEpochWithToast()
              toast.success('Baseline epoch updated.')
            } catch (error) {
              const message =
                error instanceof Error
                  ? error.message
                  : 'Could not update baseline epoch.'
              toast.error(`Failed to update baseline epoch: ${message}`)
              throw error
            }
          }}
          onClearBaselineEpoch={async () => {
            try {
              await clearBaselineEpochMutation.mutateAsync({
                clientId: client.id,
                acknowledgedWarning: true,
              })
              await refetchBaselineEpochWithToast()
              toast.success(
                'Baseline epoch cleared. Recommendation engine is off.',
              )
            } catch (error) {
              const message =
                error instanceof Error
                  ? error.message
                  : 'Could not clear baseline epoch.'
              toast.error(`Failed to clear baseline epoch: ${message}`)
              throw error
            }
          }}
        />

        {/* Manage Settings — destructive actions, collapsed by default */}
        <section className="border-destructive/40 bg-destructive/5 mt-8 rounded-lg border">
          <button
            type="button"
            onClick={() => setIsManageOpen((open) => !open)}
            aria-expanded={isManageOpen}
            className="flex w-full items-center justify-between gap-4 p-4 text-left"
          >
            <h3 className="text-destructive font-semibold">Manage Settings</h3>
            {isManageOpen ? (
              <ChevronUp size={18} className="text-destructive" />
            ) : (
              <ChevronDown size={18} className="text-destructive" />
            )}
          </button>
          {isManageOpen && (
            <div className="border-destructive/20 flex flex-wrap items-center justify-between gap-4 border-t p-4">
              <div>
                <p className="text-muted-foreground text-sm">
                  Demote this client back to freemium — revokes client access
                  and archives this coaching record.
                </p>
              </div>
              <Button
                variant="outline"
                className="border-destructive text-destructive hover:bg-destructive hover:text-white"
                onClick={() => setIsDemoteOpen(true)}
              >
                Demote
              </Button>
            </div>
          )}
        </section>

        <AlertDialog open={isDemoteOpen} onOpenChange={setIsDemoteOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                Demote {client.name} to freemium?
              </AlertDialogTitle>
              <AlertDialogDescription>
                This is a destructive action. It revokes their client access
                (client-only problems), and{' '}
                <strong>archives this coaching record</strong> (homework,
                check-ins and history). This cannot be undone from here.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={demoteMutation.isPending}>
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                className="bg-destructive hover:bg-destructive/90 text-white"
                disabled={demoteMutation.isPending}
                onClick={(e) => {
                  // Keep the dialog up until the mutation settles.
                  e.preventDefault()
                  demoteMutation.mutate({ userId: client.userId })
                }}
              >
                {demoteMutation.isPending ? 'Demoting…' : 'Demote & archive'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </main>
    </div>
  )
}
